import { useState } from "react";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { searchClient } from "@/utils/searchClients";
import { InstantSearch } from "react-instantsearch-hooks";
import SearchPageContent from "@/components/algoliaSearch/SearchPageContent";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { checkSearchLang } from "@/utils/global";
import useSearchData from "@/hooks/algolia/useAlgoliaSearch";
import { cleanFacetObject, transformObjectToList } from "@/utils/facetHelpers";
import { transformAlgoliaResults } from "@/utils/algolia";

const SearchPage = ({ device, facets, searchResults, selectedFilters }) => {
  const { t } = useTranslation("category");
  const { locale, query } = useRouter();
  const [page, setPage] = useState(1);
  const [selectedFilterKeys, setSelectedFilterKeys] = useState(selectedFilters);
  const [currentRefinement, setCurrentRefinement] = useState(
    process.env.NEXT_PUBLIC_ALGOLIA_IDX_AR
  );

  const {
    products,
    suggestions,
    numberOfPages,
    numberOfHits,
    queryId,
    banners,
  } = useSearchData(
    query,
    page,
    selectedFilterKeys,
    currentRefinement,
    locale,
    device,
    searchResults
  );

  return (
    <InstantSearch
      indexName={
        checkSearchLang(query?.q, locale) === "ar"
          ? process.env.NEXT_PUBLIC_ALGOLIA_IDX_AR
          : process.env.NEXT_PUBLIC_ALGOLIA_IDX_EN
      }
      searchClient={searchClient}
      insights={true}
    >
      <SearchPageContent
        {...{
          t,
          page,
          setPage,
          device,
          products,
          suggestions,
          numberOfPages,
          selectedFilterKeys,
          setSelectedFilterKeys,
          searchQuery: query.q,
          numberOfHits,
          setCurrentRefinement,
          currentRefinement,
          queryId,
          banners,
          facets,
        }}
      />
    </InstantSearch>
  );
};

export default SearchPage;

export async function getServerSideProps(context) {
  const { locale, query, req } = context;
  const searchTerm = query?.q || "";

  if (!searchTerm) {
    return {
      redirect: {
        destination: "/",
        permanent: false,
      },
    };
  }

  const userAgent = req.headers["user-agent"] || "";
  const isMobile =
    /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile|Kindle|NetFront|Silk-Accelerated|(hpw|web)OS|Fennec|Minimo|Opera M(obi|ini)|Blazer|Dolfin|Dolphin|Skyfire|Zune/i.test(
      userAgent
    );

  const regionId = +req.cookies?.region_id || 1101;
  let facetsList = [];
  let searchResults = {
    products: [],
    suggestions: [],
    numberOfPages: 0,
    numberOfHits: 0,
    queryId: "",
    banners: [],
  };

  if (searchTerm) {
    const indexName =
      checkSearchLang(searchTerm, locale) === "ar"
        ? process.env.NEXT_PUBLIC_ALGOLIA_IDX_AR
        : process.env.NEXT_PUBLIC_ALGOLIA_IDX_EN;

    const index = searchClient.initIndex(indexName);

    const results = await index.search(searchTerm, {
      clickAnalytics: true,
      facets: ["*"],
      filters: `stock_region_ids.${regionId} > 0`,
      analyticsTags: [isMobile ? "Web Mobile" : "Web"],
      analytics: true,
      hitsPerPage: 32,
      page: 0,
    });

    const facetOrdering =
      results?.renderingContent?.facetOrdering?.facets?.order || [];

    const redirectUrl = results.renderingContent?.redirect?.url;
    const rawFacets = results?.facets || {};
    const cleanedFacets = cleanFacetObject(rawFacets);
    facetsList = transformObjectToList(cleanedFacets, facetOrdering);

    if (redirectUrl) {
      return {
        redirect: {
          destination: redirectUrl,
          permanent: false,
        },
      };
    }

    searchResults = transformAlgoliaResults(results);
  }

  return {
    props: {
      device: isMobile ? "mobile" : "desktop",
      facets: facetsList,
      searchResults,
      selectedFilters: [],
      ...(await serverSideTranslations(locale, ["common", "category", "extended_warranty"])),
    },
  };
}
